import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import { PageHeader } from '../../../components/PageHeader';
import { Button, HEADER_BUTTON_DARK_BLUE } from '../../../components/PageHeader/Button';
import { Switch2 } from '../../../components/Switch2/Switch2';
import Table3, { columnHelper } from '../../../containers/Table/Table3';
import SimpleModal from '../../../containers/Modal/SimpleModal';
import { notificationActions } from '../../../components/Notification/redux/actions';
import Strings from '../../../Strings';
import ClaimSubmissionHistoryModal from './ClaimSubmissionHistoryModal';
import {
  actions,
  GET_ATHENA_CONFIGURATION_RESULT,
  GET_ATHENA_CLAIM_SUBMISSIONS_RESULT,
  RESUME_ATHENA_CLAIM_POLLING_RESULT,
  SET_ATHENA_CREDENTIALS_RESULT,
  SET_ATHENA_PRACTICE_RESULT,
  SET_ATHENA_REFERRING_PROVIDER_MAPPING_RESULT,
  SET_ATHENA_CLINICIAN_PROVIDER_MAPPING_RESULT,
} from './actions';
import { actions as clinicianRosterActions, GET_CLINICIAN_ROSTER_CONFIGURATION_RESULT } from '../ClinicianRoster/actions';

import './AthenaConfiguration.scss';

const DEFAULT_SCOPE = 'athena/service/Athenanet.MDP.*';
const EMPTY_PRACTICE = { practiceId: '', displayName: '', enabled: true };
const EMPTY_REFERRING_PROVIDER_MAPPING = { npi: '', practiceId: '', athenaReferringProviderId: '' };
const EMPTY_CLINICIAN_PROVIDER_MAPPING = { rosterMemberId: '', practiceId: '', athenaProviderId: '' };

function ResumePollingCell({ row, column }) {
  const { onResume, disabled } = column.columnDef.meta;
  return row.original.pollPausedAt ? (
    <button type="button" className="brand-blue" disabled={disabled} onClick={() => onResume(row.original)}>{Strings.athenaClaimPollingResume}</button>
  ) : '-';
}

ResumePollingCell.propTypes = {
  row: PropTypes.object.isRequired,
  column: PropTypes.object.isRequired,
};

function ClaimHistoryCell({ row, column }) {
  return <button type="button" onClick={() => column.columnDef.meta.onOpen(row.original.id)}>{Strings.history}</button>;
}

ClaimHistoryCell.propTypes = {
  row: PropTypes.object.isRequired,
  column: PropTypes.object.isRequired,
};

function AthenaConfiguration({
  organizationId,
  organizationName,
  getConfiguration,
  getClaimSubmissions,
  getClaimSubmissionHistory,
  resumeClaimPolling,
  getClinicianRosterConfiguration,
  setCredentials,
  setPractice,
  saveReferringProviderMapping,
  saveClinicianProviderMapping,
  showNotification,
}) {
  const [credentials, setCredentialsForm] = useState({
    base_url: '',
    client_id: '',
    client_secret: '',
    scope: DEFAULT_SCOPE,
  });
  const [practice, setPracticeForm] = useState(EMPTY_PRACTICE);
  const [practices, setPractices] = useState([]);
  const [referringProviderMappings, setReferringProviderMappings] = useState([]);
  const [clinicianProviderMappings, setClinicianProviderMappings] = useState([]);
  const [clinicianRosterMembers, setClinicianRosterMembers] = useState([]);
  const [claimSubmissions, setClaimSubmissions] = useState([]);
  const [claimPages, setClaimPages] = useState([null]);
  const [nextClaimCursor, setNextClaimCursor] = useState(null);
  const [isClaimsLoading, setIsClaimsLoading] = useState(false);
  const [historySubmissionId, setHistorySubmissionId] = useState(null);
  const [resumeSubmission, setResumeSubmission] = useState(null);
  const [isResuming, setIsResuming] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [practiceModalOpen, setPracticeModalOpen] = useState(false);
  const [editedPractice, setEditedPractice] = useState(null);
  const [referringProviderMappingModalOpen, setReferringProviderMappingModalOpen] = useState(false);
  const [referringProviderMapping, setReferringProviderMappingForm] = useState(EMPTY_REFERRING_PROVIDER_MAPPING);
  const [clinicianProviderMappingModalOpen, setClinicianProviderMappingModalOpen] = useState(false);
  const [clinicianProviderMapping, setClinicianProviderMappingForm] = useState(EMPTY_CLINICIAN_PROVIDER_MAPPING);

  const loadConfiguration = () => {
    setIsLoading(true);
    return Promise.all([
      getConfiguration(organizationId),
      getClaimSubmissions(organizationId),
      getClinicianRosterConfiguration(organizationId),
    ]).then(([response, submissionsResponse, rosterResponse]) => {
      if (response.type !== GET_ATHENA_CONFIGURATION_RESULT) {
        showNotification(Strings.athenaConfigurationLoadFailed, 5000);
      } else {
        const configuration = response.response;
        if (configuration.credentials) {
          setCredentialsForm(current => ({
            ...current,
            base_url: configuration.credentials.baseUrl,
            client_id: configuration.credentials.clientId,
            client_secret: configuration.credentials.clientSecret,
            scope: configuration.credentials.scope,
          }));
        }
        setPractices(configuration.practices);
        setReferringProviderMappings(configuration.referringProviderMappings || []);
        setClinicianProviderMappings(configuration.clinicianProviderMappings || []);
      }
      if (submissionsResponse.type !== GET_ATHENA_CLAIM_SUBMISSIONS_RESULT) {
        showNotification(Strings.athenaClaimSubmissionsLoadFailed, 5000);
      } else {
        setClaimSubmissions(submissionsResponse.response.submissions || []);
        setClaimPages([null]);
        setNextClaimCursor(submissionsResponse.response.nextCursor || null);
      }
      if (rosterResponse.type !== GET_CLINICIAN_ROSTER_CONFIGURATION_RESULT) {
        showNotification(Strings.accessClinicianRosterLoadFailed, 5000, true);
        setClinicianRosterMembers([]);
      } else {
        setClinicianRosterMembers(rosterResponse.response.release?.members || []);
      }
    }).finally(() => setIsLoading(false));
  };

  useEffect(() => {
    setHistorySubmissionId(null);
    loadConfiguration();
  }, [organizationId]);

  const loadClaimPage = async pages => {
    setIsClaimsLoading(true);
    try {
      const response = await getClaimSubmissions(organizationId, pages[pages.length - 1]);
      if (response.type === GET_ATHENA_CLAIM_SUBMISSIONS_RESULT) {
        setClaimSubmissions(response.response.submissions || []);
        setNextClaimCursor(response.response.nextCursor || null);
        setClaimPages(pages);
      } else {
        showNotification(Strings.athenaClaimSubmissionsLoadFailed, 5000, true);
      }
    } catch (_) {
      showNotification(Strings.athenaClaimSubmissionsLoadFailed, 5000, true);
    } finally {
      setIsClaimsLoading(false);
    }
  };

  const submitResumePolling = async () => {
    if (isResuming) return;
    setIsResuming(true);
    try {
      const response = await resumeClaimPolling(organizationId, resumeSubmission.id);
      if (response.type !== RESUME_ATHENA_CLAIM_POLLING_RESULT) {
        showNotification(Strings.athenaClaimPollingResumeFailed, 5000, true);
      } else {
        setResumeSubmission(null);
        showNotification(Strings.athenaClaimPollingResumed, 5000);
        await loadConfiguration();
      }
    } catch (_) {
      showNotification(Strings.athenaClaimPollingResumeFailed, 5000, true);
    } finally {
      setIsResuming(false);
    }
  };

  const updateCredentials = event => {
    const { name, value } = event.target;
    setCredentialsForm(current => ({ ...current, [name]: value }));
  };

  const updatePractice = event => {
    const { name, value } = event.target;
    setPracticeForm(current => ({ ...current, [name]: value }));
  };

  const saveCredentials = () => {
    setCredentials(organizationId, credentials).then(response => {
      if (response.type === SET_ATHENA_CREDENTIALS_RESULT) {
        showNotification(Strings.athenaCredentialsSaved, 5000);
        loadConfiguration();
      } else {
        showNotification(Strings.athenaConfigurationSaveFailed, 5000);
      }
    });
  };

  const savePractice = () => {
    const practiceId = Number(practice.practiceId);
    if (!Number.isInteger(practiceId) || practiceId <= 0) {
      showNotification(Strings.athenaPracticeIdInvalid, 5000);
      return;
    }

    setPractice(organizationId, {
      practiceId,
      displayName: practice.displayName || undefined,
      enabled: practice.enabled,
    }).then(response => {
      if (response.type === SET_ATHENA_PRACTICE_RESULT) {
        showNotification(practice.enabled ? Strings.athenaPracticeSaved : Strings.athenaPracticeDisabled, 5000);
        setPracticeModalOpen(false);
        setEditedPractice(null);
        setPracticeForm(EMPTY_PRACTICE);
        loadConfiguration();
      } else {
        showNotification(Strings.athenaConfigurationSaveFailed, 5000);
      }
    });
  };

  const openAddPractice = () => {
    setEditedPractice(null);
    setPracticeForm(EMPTY_PRACTICE);
    setPracticeModalOpen(true);
  };

  const openEditPractice = selectedPractice => {
    setEditedPractice(selectedPractice);
    setPracticeForm({
      practiceId: String(selectedPractice.practiceId),
      displayName: selectedPractice.displayName || '',
      enabled: selectedPractice.enabled,
    });
    setPracticeModalOpen(true);
  };

  const closePracticeModal = () => {
    setPracticeModalOpen(false);
    setEditedPractice(null);
    setPracticeForm(EMPTY_PRACTICE);
  };

  const closeReferringProviderMappingModal = () => {
    setReferringProviderMappingModalOpen(false);
    setReferringProviderMappingForm(EMPTY_REFERRING_PROVIDER_MAPPING);
  };

  const submitReferringProviderMapping = () => {
    const practiceId = Number(referringProviderMapping.practiceId);
    const athenaReferringProviderId = Number(referringProviderMapping.athenaReferringProviderId);
    if (!/^\d{10}$/.test(referringProviderMapping.npi.trim()) || !Number.isInteger(practiceId) || practiceId <= 0 || !Number.isInteger(athenaReferringProviderId) || athenaReferringProviderId <= 0) {
      showNotification(Strings.athenaReferringProviderMappingInvalid, 5000, true);
      return;
    }
    saveReferringProviderMapping(organizationId, {
      npi: referringProviderMapping.npi.trim(),
      practiceId,
      athenaReferringProviderId,
    }).then(response => {
      if (response.type !== SET_ATHENA_REFERRING_PROVIDER_MAPPING_RESULT) throw new Error('save failed');
      showNotification(Strings.athenaReferringProviderMappingSaved, 5000);
      closeReferringProviderMappingModal();
      return loadConfiguration();
    }).catch(() => showNotification(Strings.athenaConfigurationSaveFailed, 5000, true));
  };

  const openReferringProviderMapping = mapping => {
    setReferringProviderMappingForm(mapping ? {
      npi: mapping.npi,
      practiceId: String(mapping.practiceId),
      athenaReferringProviderId: String(mapping.athenaReferringProviderId),
    } : EMPTY_REFERRING_PROVIDER_MAPPING);
    setReferringProviderMappingModalOpen(true);
  };

  const submitClinicianProviderMapping = () => {
    const rosterMemberId = Number(clinicianProviderMapping.rosterMemberId);
    const practiceId = Number(clinicianProviderMapping.practiceId);
    const athenaProviderId = Number(clinicianProviderMapping.athenaProviderId);
    if (![rosterMemberId, practiceId, athenaProviderId].every(value => Number.isInteger(value) && value > 0)) {
      showNotification(Strings.athenaClinicianProviderMappingInvalid, 5000, true);
      return;
    }
    saveClinicianProviderMapping(organizationId, { rosterMemberId, practiceId, athenaProviderId }).then(response => {
      if (response.type !== SET_ATHENA_CLINICIAN_PROVIDER_MAPPING_RESULT) throw new Error('save failed');
      showNotification(Strings.athenaClinicianProviderMappingSaved, 5000);
      setClinicianProviderMappingModalOpen(false);
      setClinicianProviderMappingForm(EMPTY_CLINICIAN_PROVIDER_MAPPING);
      return loadConfiguration();
    }).catch(() => showNotification(Strings.athenaConfigurationSaveFailed, 5000, true));
  };

  const openClinicianProviderMapping = mapping => {
    setClinicianProviderMappingForm(mapping ? {
      rosterMemberId: String(mapping.rosterMemberId),
      practiceId: String(mapping.practiceId),
      athenaProviderId: String(mapping.athenaProviderId),
    } : EMPTY_CLINICIAN_PROVIDER_MAPPING);
    setClinicianProviderMappingModalOpen(true);
  };

  const closeClinicianProviderMappingModal = () => {
    setClinicianProviderMappingModalOpen(false);
    setClinicianProviderMappingForm(EMPTY_CLINICIAN_PROVIDER_MAPPING);
  };

  const orgPrefix = organizationName ? `${organizationName}: ` : '';
  const credentialsComplete = credentials.base_url && credentials.client_id && credentials.client_secret;
  const formatPractice = practiceId => {
    const configuredPractice = practices.find(item => item.practiceId === practiceId);
    return configuredPractice?.displayName ? `${configuredPractice.displayName} (${practiceId})` : practiceId;
  };
  const renderPracticeOptions = () => practices.map(configuredPractice => (
    <option key={configuredPractice.practiceId} value={configuredPractice.practiceId}>
      {formatPractice(configuredPractice.practiceId)}
    </option>
  ));
  const practiceColumns = [
    columnHelper.accessor('practiceId', {
      header: Strings.athenaPracticeId,
      cell: info => info.getValue(),
    }),
    columnHelper.accessor('displayName', {
      header: Strings.athenaPracticeDisplayName,
      cell: info => info.getValue() || '-',
    }),
    columnHelper.accessor('enabled', {
      header: Strings.athenaPracticeEnabled,
      cell: info => info.getValue() ? Strings.yes : Strings.no,
    }),
  ];
  const practiceButtons = [{
    icon: 'edit',
    onClick: (_, selectedPractice) => openEditPractice(selectedPractice),
    text: Strings.edit,
  }];
  const referringProviderMappingColumns = [
    columnHelper.accessor('npi', { header: Strings.athenaReferringProviderMappingNpi }),
    columnHelper.accessor('practiceId', { header: Strings.athenaPracticeId, cell: info => formatPractice(info.getValue()) }),
    columnHelper.accessor('athenaReferringProviderId', { header: Strings.athenaReferringProviderId }),
  ];
  const referringProviderMappingButtons = [{
    icon: 'edit',
    onClick: (_, mapping) => openReferringProviderMapping(mapping),
    text: Strings.edit,
  }];
  const clinicianProviderMappingColumns = [
    columnHelper.accessor('rosterMemberId', {
      header: Strings.accessClinicianRosterClinician,
      cell: info => {
        const rosterMember = clinicianRosterMembers.find(member => member.id === info.getValue());
        return rosterMember ? `${rosterMember.display_name} (${rosterMember.npi})` : `#${info.getValue()}`;
      },
    }),
    columnHelper.accessor('practiceId', {
      header: Strings.athenaPracticeId,
      cell: info => formatPractice(info.getValue()),
    }),
    columnHelper.accessor('athenaProviderId', { header: Strings.athenaProviderId }),
  ];
  const clinicianProviderMappingButtons = [{
    icon: 'edit',
    onClick: (_, mapping) => openClinicianProviderMapping(mapping),
    text: Strings.edit,
  }];
  const claimSubmissionColumns = [
    columnHelper.display({ id: 'history', header: Strings.history, cell: ClaimHistoryCell, meta: { onOpen: setHistorySubmissionId } }),
    columnHelper.accessor('dispositionId', { header: Strings.athenaClaimSubmissionDispositionId }),
    columnHelper.accessor('patientId', { header: Strings.athenaClaimSubmissionPatientId }),
    columnHelper.accessor('gCode', { header: Strings.athenaClaimSubmissionGCode }),
    columnHelper.accessor('serviceDate', { header: Strings.athenaClaimSubmissionDos }),
    columnHelper.accessor('status', { header: Strings.athenaClaimSubmissionStatus }),
    columnHelper.accessor('athenaClaimId', { header: Strings.athenaClaimSubmissionAthenaClaimId, cell: info => info.getValue() || '-' }),
    columnHelper.accessor('attemptCount', { header: Strings.athenaClaimSubmissionAttempts }),
    columnHelper.accessor('errorMessage', { header: Strings.athenaClaimSubmissionError, cell: info => info.getValue() || '-' }),
    columnHelper.accessor('externalStatus', { header: Strings.athenaClaimSubmissionExternalStatus, cell: info => info.getValue() || '-' }),
    columnHelper.accessor('externalStatusNote', { header: Strings.athenaClaimSubmissionExternalStatusNote, cell: info => info.getValue() || '-' }),
    columnHelper.accessor('pollAttemptCount', { header: Strings.athenaClaimSubmissionPollAttempts }),
    columnHelper.accessor('pollErrorMessage', { header: Strings.athenaClaimSubmissionPollError, cell: info => info.getValue() || '-' }),
    columnHelper.accessor('pollPauseReason', { header: Strings.athenaClaimPollingPauseReason, cell: info => info.getValue() || '-' }),
    columnHelper.display({
      id: 'resume-polling',
      header: Strings.athenaClaimPollingResume,
      cell: ResumePollingCell,
      meta: { onResume: setResumeSubmission, disabled: isResuming },
    }),
    columnHelper.display({
      id: 'timestamps',
      header: Strings.athenaClaimSubmissionTimestamps,
      cell: info => {
        const submission = info.row.original;
        return `${Strings.createdAt}: ${submission.createdAt}; ${Strings.athenaClaimSubmissionClaimedAt}: ${submission.claimedAt || '-'}; ${Strings.athenaClaimSubmissionSubmittedAt}: ${submission.submittedAt || '-'}; ${Strings.athenaClaimSubmissionLastPolledAt}: ${submission.lastPolledAt || '-'}; ${Strings.athenaClaimSubmissionNextPollAt}: ${submission.nextPollAt || '-'}; ${Strings.athenaClaimPollingPausedAt}: ${submission.pollPausedAt || '-'}; ${Strings.athenaClaimSubmissionTerminalAt}: ${submission.terminalAt || '-'}; ${Strings.updatedAt}: ${submission.updatedAt}`;
      },
    }),
  ];

  return (
    <>
      <PageHeader left={orgPrefix + Strings.athenaConfigurationTab} />
      <div className="athena-configuration">
        <div className="athena-configuration__group">
          <div className="athena-configuration__field">
            <label className="athena-configuration__label" htmlFor="athena-base-url">{Strings.athenaBaseUrl}</label>
            <input className="athena-configuration__input" id="athena-base-url" name="base_url" value={credentials.base_url} onChange={updateCredentials} placeholder="https://api.preview.platform.athenahealth.com" />
          </div>
          <div className="athena-configuration__field">
            <label className="athena-configuration__label" htmlFor="athena-client-id">{Strings.athenaClientId}</label>
            <input className="athena-configuration__input" id="athena-client-id" name="client_id" value={credentials.client_id} onChange={updateCredentials} />
          </div>
          <div className="athena-configuration__field">
            <label className="athena-configuration__label" htmlFor="athena-client-secret">{Strings.athenaClientSecret}</label>
            <input className="athena-configuration__input" id="athena-client-secret" name="client_secret" type="password" value={credentials.client_secret} onChange={updateCredentials} />
          </div>
          <div className="athena-configuration__field">
            <label className="athena-configuration__label" htmlFor="athena-scope">{Strings.athenaScope}</label>
            <input className="athena-configuration__input" id="athena-scope" name="scope" value={credentials.scope} onChange={updateCredentials} />
          </div>
        </div>
      </div>
      <div className="buttons">
        <Button class={HEADER_BUTTON_DARK_BLUE} title={Strings.save} onClick={saveCredentials} disabled={!credentialsComplete} />
      </div>

      <div className="athena-configuration">
        <div className="athena-configuration__section-header">
          <h2 className="athena-configuration__section-title">{Strings.athenaPractices}</h2>
          <Button class={HEADER_BUTTON_DARK_BLUE} title={Strings.add} onClick={openAddPractice} />
        </div>
      </div>
      <div className="athena-configuration__table">
        <Table3 name="athena-practices" data={practices} columns={practiceColumns} buttons={practiceButtons} isLoading={isLoading} noPagination />
      </div>
      {practiceModalOpen && (
        <SimpleModal
          name="athena-practice-modal"
          title={editedPractice ? Strings.editAthenaPractice : Strings.addAthenaPractice}
          onCancel={closePracticeModal}
          onSubmit={savePractice}
          isSubmitDisabled={!practice.practiceId}
          content={(
            <div className="athena-practice-modal">
              <div className="athena-practice-modal__field">
                <label htmlFor="athena-practice-id">{Strings.athenaPracticeId}</label>
                <input id="athena-practice-id" name="practiceId" type="text" inputMode="numeric" value={practice.practiceId} onChange={updatePractice} readOnly={!!editedPractice} />
              </div>
              <div className="athena-practice-modal__field">
                <label htmlFor="athena-practice-name">{Strings.athenaPracticeDisplayName}</label>
                <input id="athena-practice-name" name="displayName" value={practice.displayName} onChange={updatePractice} />
              </div>
              <div className="athena-practice-modal__field">
                <span className="athena-practice-modal__label">{Strings.athenaPracticeEnabled}</span>
                <Switch2 checked={practice.enabled} name="enabled" onChange={(switchComponent, enabled) => setPracticeForm(current => ({ ...current, enabled }))} />
              </div>
            </div>
          )}
        />
      )}
      <div className="athena-configuration">
        <div className="athena-configuration__section-header">
          <h2 className="athena-configuration__section-title">{Strings.athenaReferringProviderMappings}</h2>
          <Button class={HEADER_BUTTON_DARK_BLUE} title={Strings.add} onClick={() => openReferringProviderMapping()} />
        </div>
        <p>{Strings.athenaReferringProviderMappingsHint}</p>
      </div>
      <div className="athena-configuration__table">
        <Table3 name="athena-referring-provider-mappings" data={referringProviderMappings} columns={referringProviderMappingColumns} buttons={referringProviderMappingButtons} isLoading={isLoading} noPagination />
      </div>
      {referringProviderMappingModalOpen && (
        <SimpleModal
          name="athena-referring-provider-mapping-modal"
          title={Strings.athenaReferringProviderMappings}
          onCancel={closeReferringProviderMappingModal}
          onSubmit={submitReferringProviderMapping}
          isSubmitDisabled={!referringProviderMapping.npi || !referringProviderMapping.practiceId || !referringProviderMapping.athenaReferringProviderId}
          content={(
            <div className="athena-practice-modal">
              <div className="athena-practice-modal__field">
                <label htmlFor="athena-referring-provider-npi">{Strings.athenaReferringProviderMappingNpi}</label>
                <input id="athena-referring-provider-npi" value={referringProviderMapping.npi} onChange={event => setReferringProviderMappingForm(current => ({ ...current, npi: event.target.value }))} />
              </div>
              <div className="athena-practice-modal__field">
                <label htmlFor="athena-referring-provider-practice-id">{Strings.athenaPracticeId}</label>
                <select id="athena-referring-provider-practice-id" value={referringProviderMapping.practiceId} onChange={event => setReferringProviderMappingForm(current => ({ ...current, practiceId: event.target.value }))}>
                  <option value="" disabled>{Strings.athenaSelectPractice}</option>
                  {renderPracticeOptions()}
                </select>
              </div>
              <div className="athena-practice-modal__field">
                <label htmlFor="athena-referring-provider-id">{Strings.athenaReferringProviderId}</label>
                <input id="athena-referring-provider-id" inputMode="numeric" value={referringProviderMapping.athenaReferringProviderId} onChange={event => setReferringProviderMappingForm(current => ({ ...current, athenaReferringProviderId: event.target.value }))} />
              </div>
            </div>
          )}
        />
      )}
      <div className="athena-configuration">
        <div className="athena-configuration__section-header">
          <h2 className="athena-configuration__section-title">{Strings.athenaClinicianProviderMappings}</h2>
          <Button class={HEADER_BUTTON_DARK_BLUE} title={Strings.add} onClick={() => openClinicianProviderMapping()} />
        </div>
        <p>{Strings.athenaClinicianProviderMappingsHint}</p>
      </div>
      <div className="athena-configuration__table">
        <Table3 name="athena-clinician-provider-mappings" data={clinicianProviderMappings} columns={clinicianProviderMappingColumns} buttons={clinicianProviderMappingButtons} isLoading={isLoading} noPagination />
      </div>
      <div className="athena-configuration">
        <div className="athena-configuration__section-header">
          <h2 className="athena-configuration__section-title">{Strings.athenaClaimSubmissions}</h2>
        </div>
      </div>
      <div className="athena-configuration__table">
        <Table3
          name="athena-claim-submissions"
          data={claimSubmissions}
          columns={claimSubmissionColumns}
          pagination={{ offset: 0, limit: 50, totalRecords: claimSubmissions.length }}
          isLoading={isLoading || isClaimsLoading}
          noPagination
        />
        <div className="buttons">
          <button type="button" disabled={isLoading || isClaimsLoading || claimPages.length === 1} onClick={() => loadClaimPage(claimPages.slice(0, -1))}>{Strings.previous}</button>
          <button type="button" disabled={isLoading || isClaimsLoading || !nextClaimCursor} onClick={() => loadClaimPage([...claimPages, nextClaimCursor])}>{Strings.next}</button>
        </div>
      </div>
      {historySubmissionId && (
        <ClaimSubmissionHistoryModal
          key={`${organizationId}/${historySubmissionId}`}
          organizationId={organizationId}
          submissionId={historySubmissionId}
          getHistory={getClaimSubmissionHistory}
          showNotification={showNotification}
          onClose={() => setHistorySubmissionId(null)}
        />
      )}
      {resumeSubmission && (
        <SimpleModal
          name="athena-resume-polling-modal"
          title={Strings.athenaClaimPollingResume}
          submitText={Strings.athenaClaimPollingResume}
          onCancel={() => { if (!isResuming) setResumeSubmission(null); }}
          onSubmit={submitResumePolling}
          isSubmitDisabled={isResuming}
          content={<p>{`${Strings.athenaClaimSubmissionDispositionId}: ${resumeSubmission.dispositionId}. ${Strings.athenaClaimPollingResumeWarning}`}</p>}
        />
      )}
      {clinicianProviderMappingModalOpen && (
        <SimpleModal
          name="athena-clinician-provider-mapping-modal"
          title={Strings.athenaClinicianProviderMappings}
          onCancel={closeClinicianProviderMappingModal}
          onSubmit={submitClinicianProviderMapping}
          isSubmitDisabled={!clinicianProviderMapping.rosterMemberId || !clinicianProviderMapping.practiceId || !clinicianProviderMapping.athenaProviderId}
          content={(
            <div className="athena-practice-modal">
              <div className="athena-practice-modal__field">
                <label htmlFor="athena-roster-member-id">{Strings.accessClinicianRosterClinician}</label>
                <select id="athena-roster-member-id" value={clinicianProviderMapping.rosterMemberId} onChange={event => setClinicianProviderMappingForm(current => ({ ...current, rosterMemberId: event.target.value }))}>
                  <option value="" disabled>{Strings.athenaSelectRosterMember}</option>
                  {clinicianProviderMapping.rosterMemberId && !clinicianRosterMembers.some(member => String(member.id) === clinicianProviderMapping.rosterMemberId) && (
                    <option value={clinicianProviderMapping.rosterMemberId}>{`${Strings.athenaRosterMemberNotCurrent} (#${clinicianProviderMapping.rosterMemberId})`}</option>
                  )}
                  {clinicianRosterMembers.map(member => <option key={member.id} value={member.id}>{`${member.display_name} (${member.npi})`}</option>)}
                </select>
                {!clinicianRosterMembers.length && <span className="athena-practice-modal__hint">{Strings.athenaClinicianRosterNotConfigured}</span>}
              </div>
              <div className="athena-practice-modal__field">
                <label htmlFor="athena-clinician-practice-id">{Strings.athenaPracticeId}</label>
                <select id="athena-clinician-practice-id" value={clinicianProviderMapping.practiceId} onChange={event => setClinicianProviderMappingForm(current => ({ ...current, practiceId: event.target.value }))}>
                  <option value="" disabled>{Strings.athenaSelectPractice}</option>
                  {renderPracticeOptions()}
                </select>
              </div>
              <div className="athena-practice-modal__field">
                <label htmlFor="athena-provider-id">{Strings.athenaProviderId}</label>
                <input id="athena-provider-id" inputMode="numeric" value={clinicianProviderMapping.athenaProviderId} onChange={event => setClinicianProviderMappingForm(current => ({ ...current, athenaProviderId: event.target.value }))} />
              </div>
            </div>
          )}
        />
      )}
    </>
  );
}

AthenaConfiguration.propTypes = {
  organizationId: PropTypes.string.isRequired,
  organizationName: PropTypes.string,
  getConfiguration: PropTypes.func.isRequired,
  getClaimSubmissions: PropTypes.func.isRequired,
  getClaimSubmissionHistory: PropTypes.func.isRequired,
  resumeClaimPolling: PropTypes.func.isRequired,
  getClinicianRosterConfiguration: PropTypes.func.isRequired,
  setCredentials: PropTypes.func.isRequired,
  setPractice: PropTypes.func.isRequired,
  saveReferringProviderMapping: PropTypes.func.isRequired,
  saveClinicianProviderMapping: PropTypes.func.isRequired,
  showNotification: PropTypes.func.isRequired,
};

const mapDispatchToProps = dispatch => ({
  getConfiguration: organizationId => dispatch(actions.getConfiguration(organizationId)),
  getClaimSubmissions: (organizationId, cursor) => dispatch(actions.getClaimSubmissions(organizationId, cursor)),
  getClaimSubmissionHistory: (organizationId, submissionId, cursors) => dispatch(actions.getClaimSubmissionHistory(organizationId, submissionId, cursors)),
  resumeClaimPolling: (organizationId, submissionId) => dispatch(actions.resumeClaimPolling(organizationId, submissionId)),
  getClinicianRosterConfiguration: organizationId => dispatch(clinicianRosterActions.getConfiguration(organizationId)),
  setCredentials: (organizationId, credentials) => dispatch(actions.setCredentials(organizationId, credentials)),
  setPractice: (organizationId, practice) => dispatch(actions.setPractice(organizationId, practice)),
  saveReferringProviderMapping: (organizationId, mapping) => dispatch(actions.setReferringProviderMapping(organizationId, mapping)),
  saveClinicianProviderMapping: (organizationId, mapping) => dispatch(actions.setClinicianProviderMapping(organizationId, mapping)),
  showNotification: (message, timeout, isError) => dispatch(notificationActions.show(message, timeout, isError)),
});

export default connect(null, mapDispatchToProps)(AthenaConfiguration);
